import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosClient from '../api/axiosClient';
import { toast } from 'react-hot-toast';
import Swal from 'sweetalert2';
import { Loader2 } from 'lucide-react';
import '../styles/Homepage.css';
import VideoDemoModal from "./Videodemo";
import logo from "../assets/Logo/codematrixLogo.png";

const FEATURES = [
  { icon: '🖥️', tint: 'purple', title: 'AI Mock Interviews', desc: 'Realistic AI interviews with role-specific questions and instant feedback.' },
  { icon: '📚', tint: 'pink', title: 'Interview Question Library', desc: 'Curated questions across companies, roles, and difficulty levels.' },
  { icon: '⌘', tint: 'green', title: 'Coding Practice', desc: 'Practice coding problems with AI evaluation and hints.' },
  { icon: '📄', tint: 'orange', title: 'Resume Review', desc: 'AI-powered resume review with actionable suggestions to stand out.' },
  { icon: '📊', tint: 'blue', title: 'Dashboard Analytics', desc: 'Detailed analytics to measure performance and identify weak areas.' },
  { icon: '🧭', tint: 'yellow', title: 'Learning Roadmap', desc: 'Personalized roadmap based on your goals and current skill level.' },
  { icon: '📈', tint: 'teal', title: 'Progress Tracking', desc: 'Track your progress over time with insights and recommendations.' },
  { icon: '🏆', tint: 'green', title: 'Achievements', desc: 'Unlock achievements and stay motivated as you improve.' },
];

const STEPS = [
  { icon: '👤', title: 'Sign Up', desc: 'Create your free account in seconds.' },
  { icon: '🎯', title: 'Set Your Goal', desc: 'Choose your role and set your interview goal.' },
  { icon: '💬', title: 'Practice with AI', desc: 'Take AI mock interviews and solve questions.' },
  { icon: '📶', title: 'Track Progress', desc: 'Get insights and track your improvement.' },
  { icon: '✅', title: 'Get Interview Ready', desc: 'Build confidence and crack your dream role.' },
];

const EXPLORE = [
  {
    title: 'Dashboard Overview',
    desc: 'Track your performance and progress',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80',
    alt: 'Analytics dashboard with charts and graphs on a screen',
  },
  {
    title: 'AI Mock Interview',
    desc: 'Real-time AI interview with instant feedback',
    image: 'https://blog.offergoose.com/post104/image.png',
    alt: 'Person on a video call interview with a laptop',
  },
  {
    title: 'Coding Practice',
    desc: 'Practice coding with AI evaluation',
    image: 'https://images.unsplash.com/photo-1587620962725-abab7fe55159?auto=format&fit=crop&w=800&q=80',
    alt: 'Code editor with syntax-highlighted code on a dark theme',
  },
  {
    title: 'Analytics & Reports',
    desc: 'Detailed analytics and performance insights',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80',
    alt: 'Bar chart analytics report on a laptop screen',
  },
];

const TESTIMONIALS = [
  { quote: 'Codematrix helped me identify my weak areas and improve drastically. I cleared my interviews!', name: 'Rohit Sharma', role: 'SDE II at Google', color: '#6C4CE0' },
  { quote: 'The AI mock interviews are so realistic and the feedback is super helpful.', name: 'Sneha Verma', role: 'SDE at Microsoft', color: '#21A868' },
  { quote: 'Solved over 500+ coding questions and got placed at Amazon. Highly recommended!', name: 'Amit Patil', role: 'SDE at Amazon', color: '#F0A63D' },
  { quote: 'The roadmap and feedback system kept me focused throughout my prep.', name: 'Priya Singh', role: 'SDE at Deloitte', color: '#D64C8C' },
];

const PLANS = [
  {
    name: 'Free',
    amount: '₹0',
    displayPrice: '₹0',
    note: 'Forever',
    popular: false,
    isTeam: true,
    features: [
      '3 AI Mock Interviews / month',
      'Access to Question Library',
      'Basic Analytics',
      'Limited Resume Review'
    ],
    cta: 'Get Started',
  },
  {
    name: 'Pro',
    amount: '₹4999',
    displayPrice: '₹499',
    note: 'Everything in Free, plus',
    popular: true,
    isTeam: false,
    features: [
      'Unlimited AI Mock Interviews',
      'Unlimited Coding Practice',
      'Advanced Analytics',
      'Full Resume Review',
      'Personalized Roadmap',
      'Priority Support'
    ],
    cta: 'Get Pro',
  },
  {
    name: 'Pro Plus',
    amount: '₹7999',
    displayPrice: '₹799',
    note: 'Everything in Pro, plus',
    popular: false,
    isTeam: false,
    features: [
      'AI Interviewer (Voice)',
      'Behavioral Question Practice',
      'System Design Practice',
      'Portfolio Booster',
      'Doubt Priority Support'
    ],
    cta: 'Get Pro Plus',
  },
  {
    name: 'Premium',
    amount: '₹9999',
    displayPrice: '₹999',
    note: 'Everything in Pro Plus, plus',
    popular: false,
    isTeam: false,
    features: [
      'ATS Score & Resume Optimization',
      'Company-wise Interview Preparation',
      '1-on-1 Expert Mock Interview',
      'AI Career Mentor',
      'Lifetime Interview Performance Analytics',
      'Early Access to New AI Features',
      '24×7 Premium Support'
    ],
    cta: 'Get Premium',
  },
];

const FAQ_LEFT = [
  { q: 'What is Codematrix?', a: 'Codematrix is an AI-powered interview and learning platform that helps you practice mock interviews, sharpen coding skills, and track your progress toward your dream role.' },
  { q: 'How do AI mock interviews work?', a: 'You pick a role and difficulty, the AI interviewer asks role-specific questions in real time, and you get instant, actionable feedback right after the session.' },
  { q: 'Is there a free plan available?', a: 'Yes — the Free plan gives you a handful of mock interviews and question-library access every month at no cost.' },
  { q: 'Can I track my progress?', a: 'Your dashboard tracks scores, questions solved, and skill trends over time so you always know what to focus on next.' },
];

const FAQ_RIGHT = [
  { q: 'Are the interview questions role-specific?', a: 'Yes — questions are tailored to the role, company type, and seniority level you select.' },
  { q: 'How accurate is the AI feedback?', a: 'Feedback is generated against rubrics built from real interview patterns, covering both technical correctness and communication.' },
  { q: 'Can I use it for multiple roles?', a: 'You can switch your target role and difficulty at any time — your history and roadmap adjust with you.' },
  { q: 'Is my data secure?', a: 'Your practice sessions and personal data are encrypted and never shared with third parties.' },
];

/* ---------------- Hero panel animation helpers ---------------- */

function useCountUp(target, duration = 1400, delay = 0) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setValue(target);
      return;
    }
    let raf;
    let start;
    const timer = setTimeout(() => {
      const tick = (now) => {
        if (!start) start = now;
        const p = Math.min((now - start) / duration, 1);
        const eased = 1 - Math.pow(1 - p, 3); // ease-out cubic
        setValue(Math.round(target * eased));
        if (p < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, delay);

    return () => {
      clearTimeout(timer);
      cancelAnimationFrame(raf);
    };
  }, [target, duration, delay]);

  return value;
}

function AnimatedStat({ label, target, prefix = '', suffix = '', delta, delay = 0 }) {
  const value = useCountUp(target, 1400, delay);
  const deltaValue = useCountUp(delta, 1400, delay);
  return (
    <div className="cmxl-stat cmxl-anim-in" style={{ animationDelay: `${delay}ms` }}>
      <div className="cmxl-stat__label">{label}</div>
      <div className="cmxl-stat__value">{prefix}{value}{suffix}</div>
      <div className="cmxl-stat__delta">▲ {deltaValue}% vs last week</div>
    </div>
  );
}

function SkillBar({ label, target, gain, delay = 0 }) {
  const [ready, setReady] = useState(false);
  const gainValue = useCountUp(gain, 1400, delay);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), delay + 100);
    return () => clearTimeout(t);
  }, [delay]);

  return (
    <div>
      <div className="cmxl-skill-bar__label"><span>{label}</span><span>+{gainValue}%</span></div>
      <div className="cmxl-skill-bar__track">
        <div
          className="cmxl-skill-bar__fill"
          style={{ width: ready ? `${target}%` : '0%' }}
        />
      </div>
    </div>
  );
}

const ACTIVITIES = [
  'Completed System Design mock — scored 61/100',
  'Solved 20 coding questions in Data Structures',
  'Unlocked achievement: 7-day streak 🔥',
  'Resume review finished — 4 suggestions',
];

function ActivityTicker() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((n) => (n + 1) % ACTIVITIES.length), 3000);
    return () => clearInterval(id);
  }, []);
  return (
    <div key={i} className="cmxl-ticker-item">{ACTIVITIES[i]}</div>
  );
}

function HeroPanel() {
  return (
    <div className="cmxl-panel cmxl-panel--float" role="img" aria-label="Preview of the Codematrix progress dashboard">
      <div className="cmxl-panel__top">
        <div className="cmxl-panel__brand">
         <img src={logo} alt="Codematrix Logo" style={{ width: 30, height: 30, borderRadius: 7, marginRight: 6 }} />
          Codematrix
         
        </div>
        <div className="cmxl-panel__welcome" style={{ textAlign: 'right' }}>
          <strong>Welcome , Zesty<span className="cmxl-wave">👋</span></strong>
          Interview prep progress
        </div>
      </div>

      <div className="cmxl-stat-row">
        <AnimatedStat label="Interviews" target={85} delta={12} delay={100} />
        <AnimatedStat label="Avg. Score" target={78} suffix="/100" delta={6} delay={200} />
        <AnimatedStat label="Questions" target={240} delta={18} delay={300} />
        <AnimatedStat label="Percentile" prefix="Top " target={12} suffix="%" delta={4} delay={400} />
      </div>

      <div className="cmxl-panel__row">
        <div className="cmxl-card-mini cmxl-anim-in" style={{ animationDelay: '500ms' }}>
          <div className="cmxl-card-mini__title">Performance trend</div>
          <div className="cmxl-spark-wrap">
            <svg className="cmxl-sparkline" viewBox="0 0 220 64" preserveAspectRatio="none">
              <polygon
                className="cmxl-spark-area"
                points="0,46 30,38 60,44 90,22 120,30 150,14 180,20 220,6 220,64 0,64"
                fill="#6C4CE0"
                opacity="0.08"
              />
              <polyline
                className="cmxl-spark-line"
                pathLength="1"
                points="0,46 30,38 60,44 90,22 120,30 150,14 180,20 220,6"
                fill="none"
                stroke="#6C4CE0"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="cmxl-spark-dot" />
          </div>
        </div>
        <div className="cmxl-card-mini cmxl-anim-in" style={{ animationDelay: '600ms' }}>
          <div className="cmxl-card-mini__title">Next mock interview</div>
          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4 }}>System Design</div>
          <div style={{ fontSize: 11, color: 'var(--cmxl-ink-faint)' }}>In 3 days · 60 min</div>
        </div>
      </div>

      <div className="cmxl-panel__row">
        <div className="cmxl-card-mini cmxl-anim-in" style={{ animationDelay: '700ms' }}>
          <div className="cmxl-card-mini__title">Recent activity</div>
          <div className="cmxl-ticker">
            <ActivityTicker />
          </div>
        </div>
        <div className="cmxl-card-mini cmxl-anim-in" style={{ animationDelay: '800ms' }}>
          <div className="cmxl-card-mini__title">Skill improvement</div>
          <div className="cmxl-skill-bars">
            <SkillBar label="Problem solving" target={78} gain={22} delay={900} />
            <SkillBar label="Communication" target={60} gain={10} delay={1050} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Hero() {
  const [showDemoVideo, setShowDemoVideo] = useState(false);
  return (
    <section className="cmxl-section cmxl-hero">
      <div className="cmxl-container cmxl-hero__grid">
        <div>
          <span className="cmxl-eyebrow">★ AI-Powered Interview &amp; Learning Platform</span>
          <h1 className="cmxl-hero__title">
            Understand What&rsquo;s Holding You Back in <span>Interviews</span>
          </h1>
          <p className="cmxl-hero__lede">
            AI mock interviews, instant feedback, and personalized roadmaps to help you improve your skills and ace every opportunity.
          </p>
          <div className="cmxl-hero__ctas">
            <a href="/daily" className="cmxl-btn cmxl-btn--primary">Get Started Free</a>
          </div>
          <div className="cmxl-hero__trust">
            <div className="cmxl-avatar-stack">
              {TESTIMONIALS.slice(0, 5).map((t, i) => (
                <span key={i} style={{ background: t.color }}>
                  {t.name.split(' ').map(n => n[0]).join('')}
                </span>
              ))}
            </div>
            <div className="cmxl-hero__trust-copy">
              <strong>Trusted by 10,000+ learners</strong><br />
              across 300+ companies
            </div>
          </div>
        </div>

        <HeroPanel />
        {showDemoVideo && (
          <VideoDemoModal
            videoSrc="/demo.mp4"
            onClose={() => setShowDemoVideo(false)}
          />
        )}
      </div>
    </section>
  );
}

function LogoStrip() {
  const logos = ['Google', 'Microsoft', 'Amazon', 'TCS', 'Infosys', 'Accenture', 'Cognizant', 'Deloitte'];
  return (
    <section className="cmxl-logos">
      <div className="cmxl-container">
        <div className="cmxl-logos__label">Trusted by learners from</div>
        <div className="cmxl-logos__row">
          {logos.map((l) => <span key={l}>{l}</span>)}
        </div>
      </div>
    </section>
  );
}

const TINTS = {
  purple: { bg: '#EFE9FD', fg: '#6C4CE0' },
  pink: { bg: '#FCE7F3', fg: '#D64C8C' },
  green: { bg: '#DCFCE7', fg: '#21A868' },
  orange: { bg: '#FFEDD5', fg: '#F0A63D' },
  blue: { bg: '#DBEAFE', fg: '#3B82F6' },
  yellow: { bg: '#FEF3C7', fg: '#CA9A1E' },
  teal: { bg: '#CCFBF1', fg: '#0F9488' },
};

function Features() {
  return (
    <section className="cmxl-section">
      <div className="cmxl-container">
        <h2 className="cmxl-heading-center">Everything You Need to Crack Interviews</h2>
        <div className="cmxl-feature-grid">
          {FEATURES.map((f) => {
            const tint = TINTS[f.tint];
            return (
              <div className="cmxl-feature-card" key={f.title}>
                <div className="cmxl-feature-card__icon" style={{ background: tint.bg, color: tint.fg }}>{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Journey() {
  return (
    <section className="cmxl-section cmxl-section--soft">
      <div className="cmxl-container">
        <h2 className="cmxl-heading-center">Your Journey to Success</h2>
        <div className="cmxl-journey">
          {STEPS.map((s, i) => (
            <div className="cmxl-journey__step" key={s.title}>
              {i < STEPS.length - 1 && <div className="cmxl-journey__line" />}
              <div className="cmxl-journey__badge">{String(i + 1).padStart(2, '0')}</div>
              <div className="cmxl-journey__icon">{s.icon}</div>
              <h3>{s.title}</h3>
              <p>{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Explore() {
  return (
    <section className="cmxl-section">
      <div className="cmxl-container">
        <h2 className="cmxl-heading-center">Explore the Platform</h2>
        <div className="cmxl-explore">
          <div className="cmxl-explore__track">
            {EXPLORE.map((card) => (
              <div className="cmxl-explore__card" key={card.title}>
                <div className="cmxl-explore__frame">
                  <div className="cmxl-explore__frame-dot-row">
                    <span /><span /><span />
                  </div>
                  <div className="cmxl-explore__frame-body" style={{ padding: 0, overflow: 'hidden' }}>
                    <img
                      src={card.image}
                      alt={card.alt}
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: 8 }}
                    />
                  </div>
                </div>
                <h4>{card.title}</h4>
                <p>{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  return (
    <section className="cmxl-section cmxl-section--soft">
      <div className="cmxl-container">
        <h2 className="cmxl-heading-center">Loved by Learners Placed in Top Companies</h2>
        <div className="cmxl-testimonials">
          {TESTIMONIALS.map((t) => (
            <div className="cmxl-testimonial-card" key={t.name}>
              <div className="cmxl-testimonial-card__stars">★★★★★</div>
              <p>&ldquo;{t.quote}&rdquo;</p>
              <div className="cmxl-testimonial-card__person">
                <span className="cmxl-testimonial-card__avatar" style={{ background: t.color }}>
                  {t.name.split(' ').map((n) => n[0]).join('')}
                </span>
                <span>
                  <strong>{t.name}</strong>
                  <span>{t.role}</span>
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Pricing (payment logic lives in the parent, this is presentational) ---------------- */

function Pricing({ handlePayment, paymentLoading, activePlan, promoCodes, setPromoCodes }) {
  return (
    <section id="pricing-plans" className="cmxl-section">
      <div className="cmxl-container">
        <h2 className="cmxl-heading-center">Choose the Perfect Plan for You</h2>
        <div className="cmxl-pricing">
          {PLANS.map((p) => {
            const isLoadingThis = paymentLoading && activePlan === p.name;
            return (
              <div className={`cmxl-price-card${p.popular ? ' cmxl-price-card--popular' : ''}`} key={p.name}>
                {p.popular && <span className="cmxl-price-card__badge">Popular</span>}
                <div className="cmxl-price-card__name">{p.name}</div>
                <div className="cmxl-price-card__amount">
                  {p.amount}{p.name !== 'Free' && <sub> /month</sub>}
                </div>
                <div className="cmxl-price-card__note">{p.note}</div>

                <button
                  type="button"
                  disabled={isLoadingThis}
                  onClick={() => handlePayment(p)}
                  className={`cmxl-btn cmxl-btn--block ${p.popular ? 'cmxl-btn--primary' : 'cmxl-btn--ghost'}`}
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    opacity: isLoadingThis ? 0.7 : 1,
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {isLoadingThis ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : p.isTeam ? (
                    'Try Demo'
                  ) : (
                    p.cta
                  )}
                </button>

                {!p.isTeam && (
                  <input
                    type="text"
                    placeholder="Enter Promo Code"
                    value={promoCodes[p.name] || ''}
                    onChange={(e) =>
                      setPromoCodes((prev) => ({
                        ...prev,
                        [p.name]: e.target.value,
                      }))
                    }
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '10px',
                      border: '1px solid #e2e8f0',
                      margin: '12px 0',
                      fontSize: '13px',
                      outline: 'none',
                    }}
                  />
                )}

                <ul className="cmxl-price-card__features">
                  {p.features.map((f) => <li key={f}>{f}</li>)}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FaqColumn({ items, openIndex, setOpenIndex, offset }) {
  return (
    <div>
      {items.map((item, i) => {
        const idx = offset + i;
        const isOpen = openIndex === idx;
        return (
          <div className="cmxl-faq__item" key={item.q}>
            <button
              type="button"
              className="cmxl-faq__question"
              aria-expanded={isOpen}
              onClick={() => setOpenIndex(isOpen ? null : idx)}
            >
              {item.q}
              <span className={`cmxl-faq__chevron${isOpen ? ' cmxl-is-open' : ''}`}>⌄</span>
            </button>
            {isOpen && <p className="cmxl-faq__answer">{item.a}</p>}
          </div>
        );
      })}
    </div>
  );
}

function Faq() {
  const [openIndex, setOpenIndex] = useState(0);
  return (
    <section className="cmxl-section cmxl-section--soft">
      <div className="cmxl-container">
        <h2 className="cmxl-heading-center">Frequently Asked Questions</h2>
        <div className="cmxl-faq__grid">
          <FaqColumn items={FAQ_LEFT} openIndex={openIndex} setOpenIndex={setOpenIndex} offset={0} />
          <FaqColumn items={FAQ_RIGHT} openIndex={openIndex} setOpenIndex={setOpenIndex} offset={100} />
        </div>
      </div>
    </section>
  );
}

function CtaBanner() {
  const [showDemoVideo, setShowDemoVideo] = useState(false);

  return (
    <section className="cmxl-section">
      <div className="cmxl-container">
        <div className="cmxl-cta-banner">
          <div className="cmxl-cta-banner__deco" />
          <div className="cmxl-cta-banner__deco cmxl-cta-banner__deco--sm" />
          <div className="cmxl-cta-banner__copy">
            <h2>Ready to Crack Your Next Interview?</h2>
            <p>Join 10,000+ learners and take the first step towards your dream job.</p>
            <div className="cmxl-cta-banner__ctas">
              <a href="/daily" className="cmxl-btn cmxl-btn--on-brand">Get Started Free</a>
            </div>
          </div>
        </div>
      </div>
      {showDemoVideo && (
        <VideoDemoModal
          videoSrc="/demo.mp4"
          onClose={() => setShowDemoVideo(false)}
        />
      )}
    </section>
  );
}

/* ---------------- Root component: owns all payment state + logic ---------------- */

export default function CodematrixLanding() {
  const navigate = useNavigate();

  const [paymentLoading, setPaymentLoading] = useState(false);
  const [activePlan, setActivePlan] = useState(null);
  const [promoCodes, setPromoCodes] = useState({});

  const courseName = localStorage.getItem('selectedCourse') || null;

  const getDemoKey = (userId, courseName) => {
    const normalizedCourse = courseName?.toLowerCase().replace(/\s+/g, '_');
    return `demo_${userId}_${normalizedCourse}`;
  };

  const getPurchaseKey = (userId, courseName, planName) => {
    const normalizedCourse = courseName?.toLowerCase().replace(/\s+/g, '_');
    const normalizedPlan = planName.toLowerCase();
    return `purchase_${userId}_${normalizedCourse}_${normalizedPlan}`;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async (plan) => {
    const token = localStorage.getItem('token');
    const userId = localStorage.getItem('userId');

    const purchaseKey = getPurchaseKey(userId, courseName, plan.name);

    // 1) LOGIN CHECK
    if (!token) {
      Swal.fire({
        icon: 'warning',
        title: 'Login Required',
        text: 'Please login first to continue.',
        confirmButtonColor: '#6366f1',
      }).then(() => navigate('/login'));
      return;
    }

    // 2) SAME COURSE + SAME PLAN BLOCK
    const alreadyPurchased = localStorage.getItem(purchaseKey) === 'true';
    if (alreadyPurchased) {
      Swal.fire({
        icon: 'info',
        title: 'Already Purchased',
        text: 'You have already purchased this plan for this course. Try another plan.',
        confirmButtonColor: '#6366f1',
      });
      return;
    }

    // ================= FREE PLAN =================
    if (plan.name === 'Free') {
      const demoKey = getDemoKey(userId, courseName);
      const alreadyDemo = localStorage.getItem(demoKey) === 'true';

      if (alreadyDemo) {
        Swal.fire({
          icon: 'info',
          title: 'Already Activated',
          text: 'Demo already activated for this course.',
          confirmButtonColor: '#6366f1',
        });
        return;
      }

      try {
        setPaymentLoading(true);
        setActivePlan(plan.name);

        const res = await axiosClient.post(
          '/auth/api/payments/activate-free',
          null,
          {
            params: { courseName, planName: 'FREE' },
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        localStorage.setItem(demoKey, 'true');
        toast.success(res.data?.message || 'Demo activated');
        navigate('/front');
      } catch (e) {
        Swal.fire({
          icon: 'warning',
          title: 'Notice',
          text: e.response?.data?.message || 'Demo already activated',
        });
      } finally {
        setPaymentLoading(false);
        setActivePlan(null);
      }
      return;
    }

    // ================= PAID PAYMENT =================
    try {
      setPaymentLoading(true);
      setActivePlan(plan.name);

      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error('Razorpay failed to load');
        setPaymentLoading(false);
        setActivePlan(null);
        return;
      }

      // If your backend's create-order endpoint expects different plan names,
      // map them here before sending.
      const res = await axiosClient.post(
        '/auth/api/payments/create-order',
        null,
        {
          params: {
            courseName,
            planName: plan.name.toUpperCase(),
            promoCode: promoCodes[plan.name] || null,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const order = res.data.data || res.data;

      const options = {
        key: 'rzp_live_SYZNjqzmznpzEC',
        amount: order.amount,
        currency: 'INR',
        name: 'CodeMatrix',
        description: `${plan.name} Plan`,
        order_id: order.orderId || order.id,

        handler: async (rp) => {
          await axiosClient.post('/auth/api/payments/verify-payment', null, {
            params: {
              orderId: rp.razorpay_order_id,
              paymentId: rp.razorpay_payment_id,
              signatureId: rp.razorpay_signature,
              courseName,
            },
          });

          localStorage.setItem(purchaseKey, 'true');
          toast.success('Payment Successful 🎉');
          navigate('/front', { state: { selectedCourse: courseName } });
        },

        modal: {
          ondismiss: () => setPaymentLoading(false),
        },

        theme: { color: '#6366f1' },
      };

      new window.Razorpay(options).open();
    } catch (err) {
      const msg = err.response?.data?.message;

      if (msg?.toLowerCase().includes('pehle') || msg?.toLowerCase().includes('already')) {
        Swal.fire({
          icon: 'info',
          title: 'Already Purchased',
          text: msg,
          confirmButtonColor: '#6366f1',
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Payment Failed',
          text: msg || 'Something went wrong',
        });
      }
    }

    setPaymentLoading(false);
    setActivePlan(null);
  };

  return (
    <div className="cmxl-root">
      <Hero />
      <LogoStrip />
      <Features />
      <Journey />
      <Explore />
      <Testimonials />
      <Pricing
        handlePayment={handlePayment}
        paymentLoading={paymentLoading}
        activePlan={activePlan}
        promoCodes={promoCodes}
        setPromoCodes={setPromoCodes}
      />
      <Faq />
      <CtaBanner />
    </div>
  );
}